import { useState, useEffect, useContext, useRef } from "react";
import {
    DataGrid,
    Column,
    FilterRow,
    HeaderFilter,
    LoadPanel,
    Scrolling,
    SearchPanel,
    Paging,
    Selection,
    Item,
    Toolbar,
} from "devextreme-react/data-grid";
import { useRecoilState, useRecoilValue } from "recoil";
import { OidcIdentityContext } from "@rsmus/react-auth";
import { dataGridConfig, defaultPageSize } from "../../constants";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Button, Checkbox, FormControl, FormControlLabel, MenuItem, Pagination, Popover, Badge, IconButton, Select, SelectChangeEvent } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import useSnackbar from "../../hooks/useSnackbar";
import { createFolder } from "./CreateFolderAtoms";
import {
    GetAllSitesDTO,
    GetAllSitesInfoDTO,
    ServiceBusEnum,
    ServiceMessage
} from "../../api-client-nswag/taxportal-client";
import {SyncTypeEnum} from "../../api-client-nswag/create-sites-client";
import { taxPortalClientApi,createSitesClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { createFolders } from "./CreateFolders.types";
import "./CreateFolders.scss";
import { SnackbarMessage } from "../../hooks/useSnackbar.types";
import { calculateGridHeight, convertUTCTimeToLocal } from "../../util/Utils";
import AppNameToggle, { getAppName } from "../../cui/commonComponents/AppNameToggle";
import { isEmpty, isNull } from "lodash";

type DatagridRefType = DataGrid;

export const CreateFolders = () => {
    const { user } = useContext(OidcIdentityContext);
    const [data, setData] = useRecoilState<createFolders>(createFolder);
    const [selectedRows, setSelectedRows] = useState<any>([]);
    const [dropDown, setDropDown] = useState("");
    const dataGridRef = useRef<DatagridRefType>(null);
    const [pageNumber, setpageNumber] = useState(1);
    const appName = useRecoilValue(getAppName);
    const [searchTerm, setSearchTerm] = useState<string | null | undefined>(null);
    const { showSnackbar } = useSnackbar();
    const { showSuccessSnackbar } = useSnackbar();
    const [selectedRowKeys, setSelectedRowKeys] = useState<any>([]);
    const [isSiteComment, setIsSiteComment] = useState(false);
    const [isCustomPermission, setIsCustomPermission] = useState(false);
    const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
    const [filterPosition, setFilterPosition] = useState<{ top: number; left: number } | null>(null);
    const prevKeysRef = useRef<any>([]);
    const prevDataRef = useRef<any>([]);

    const fetchSites = async () => {
        try {
            if (dropDown === "Internal") {
                const startIndex = pageNumber === 1 ? 0 : (pageNumber - 1) * defaultPageSize;
                setData({ pending: true, fulfilled: true, sites: [], totalCount: 0 });
                const data = await createSitesClientApi.taxPortal_GetAllSites(
                    SyncTypeEnum.CREATE_FOLDER_INTERNAL,
                    null,
                    appName !== null ? appName : "PERF",
                    startIndex,
                    defaultPageSize,
                    isSiteComment,
                    isCustomPermission
                );
                setData({ pending: false, fulfilled: true, sites: data.data, totalCount: data.totalCount });
            }
            if (dropDown === "External") {
                const startIndex = pageNumber === 1 ? 0 : (pageNumber - 1) * defaultPageSize;
                setData({ pending: true, fulfilled: true, sites: [], totalCount: 0 });

                const data = await createSitesClientApi.taxPortal_GetAllSites(
                    SyncTypeEnum.EXTERNAL_FOLDER_CREATION,
                    null,
                    appName !== null ? appName : "PERF",
                    startIndex,
                    defaultPageSize,
                    isSiteComment,
                    isCustomPermission
                );
                setData({ pending: false, fulfilled: true, sites: data.data, totalCount: data.totalCount });
            }
        } catch (error) {
            setData({ pending: false, fulfilled: false, sites: [], totalCount: 0 });
            showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        }
    };

    const requestService = async (eventType: ServiceBusEnum, message: SnackbarMessage) => {
        const data = selectedRows.map((e: GetAllSitesInfoDTO): ServiceMessage => {
            return {
                siteId: e.hqSiteId,
                userName: user?.profile.preferred_username,
                eventType,
                mdmClientId: e.mdmClientId,
            };
        });
        try {
            showSnackbar({ message });
            await taxPortalClientApi.taxPortal_InsertTopic(data);
            clearSelection();
        } catch (error) {
            showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        }
    };

    const handleSyncClick = async () => requestService(ServiceBusEnum.SYNC_MDM, SnackbarMessage.SYNC_MDM);
    const handleClick = async () => {
        if (dropDown === "External") {
            requestService(ServiceBusEnum.EXTERNAL_FOLDER_CREATION, SnackbarMessage.CREATE_FOLDER);
        } else if (dropDown === "Internal") {
            requestService(ServiceBusEnum.INTERNAL_FOLDER_CREATION, SnackbarMessage.CREATE_FOLDER);
        }
    };

    const handleClickForPermissions = async () => {
        const data = selectedRows.map((e: GetAllSitesInfoDTO): ServiceMessage => {
            return {
                siteId: e.hqSiteId,
                userName: user?.profile.preferred_username,
                mdmClientId: e.mdmClientId,
            };
        });
        try {
            var response = await taxPortalClientApi.taxPortal_UpdateFolderPermission(data);
            if (response === true) {
                showSuccessSnackbar({ message: SnackbarMessage.FOLDER_PERMISSION_UPDATE });
            } else {
                showSnackbar({
                    message: SnackbarMessage.FOLDER_PERMISSION_UPDATE_FAIL,
                    severity: "error",
                });
            }
        } finally {
            clearSelection();
        }
    };

    const handleClickForTaxYear = async () => {
        showSuccessSnackbar({ message: SnackbarMessage.FOLDER_TAXYEAR_UPDATE, severity: "info" });
        const data = selectedRows.map((e: GetAllSitesInfoDTO): ServiceMessage => {
            return {
                siteId: e.hqSiteId,
                mdmClientId: e.mdmClientId,
            };
        });
        try {
            var response = await taxPortalClientApi.taxPortal_UpdateTaxYearInFolderMetaData(data);
            if (response === true) {
                showSuccessSnackbar({ message: SnackbarMessage.FOLDER_TAXYEAR_PROGRESS, severity: "info" });
            } else {
                showSnackbar({
                    message: SnackbarMessage.FOLDER_TAXYEAR_UPDATE_FAIL,
                    severity: "error",
                });
            }
        } finally {
            clearSelection();
        }
    };

    const onSelectionChanged = (e: any) => {
        const newPageKeys = e.selectedRowKeys;
        const newPageData = e.selectedRowsData;
        const visibleKeys = e.component.getVisibleRows().map((row: any) => row.key);

        const addedKeys = newPageKeys.filter((key: any) => !prevKeysRef.current.includes(key));
        const addedData = newPageData.filter((row: any) => addedKeys.includes(row.hqSiteId));
        const removedKeys = visibleKeys.filter((k: any) => prevKeysRef.current.includes(k) && !newPageKeys.includes(k));

        const updatedKeys = [
            ...prevKeysRef.current.filter((k: any) => !removedKeys.includes(k)),
            ...addedKeys,
        ];

        const updatedData = [
            ...prevDataRef.current.filter((row: any) => !removedKeys.includes(row.hqSiteId)),
            ...addedData,
        ];

        prevKeysRef.current = updatedKeys;
        prevDataRef.current = updatedData;

        setSelectedRowKeys(updatedKeys);
        setSelectedRows(updatedData);
    };

    const clearSelection = () => {
        setSelectedRowKeys([]);
        prevKeysRef.current = [];
        prevDataRef.current = [];
        setSelectedRows([]);
    };

    const cellRenderForMessage = (e: { data: GetAllSitesInfoDTO }) => {
        if (e.data.message === null && e.data.status === null) {
            return null;
        }
        return <div title={e.data.message}>{e.data.status}</div>;
    };

    const cellRenderForDate = (date: string) => {
        return <div>{convertUTCTimeToLocal(date)}</div>;
    };

    useEffect(() => {
        if (searchTerm === null || searchTerm?.length === 0) {
            fetchSites();
        }
    }, [dropDown, pageNumber, appName, searchTerm, isSiteComment, isCustomPermission]);

    useEffect(() => {
        setDropDown("External");
    }, []);

    const names = ["External"];

    const handleChange = (e: SelectChangeEvent) => {
        setDropDown(e.target.value);
        dataGridRef.current!.instance.clearSelection();
        clearSelection();
    };

    const refreshButtonOptions = {
        icon: "refresh",
        onClick: async () => {
            setSearchTerm(null);
            if (dataGridRef.current) {
                dataGridRef.current.instance.clearFilter();
                dataGridRef.current.instance.clearSelection();
            }
            clearSelection();
            fetchSites();
        },
    };

    const fetchSitesBySearchTerms = async () => {
        try {
            const syncType =
                dropDown === "Internal"
                    ? SyncTypeEnum.CREATE_FOLDER_INTERNAL
                    : dropDown === "External"
                        ? SyncTypeEnum.EXTERNAL_FOLDER_CREATION
                        : null;

            if (!syncType) return;

            setData({ pending: false, fulfilled: true, sites: [], totalCount: 0 });

            const res = await createSitesClientApi.taxPortal_GetAllSiteSearch(
                syncType,
                appName !== null ? appName : "PERF",
                searchTerm,
                isSiteComment,
                isCustomPermission
            );

            setData({
                pending: false,
                fulfilled: true,
                sites: res?.data || [],
                totalCount: res?.totalCount || 0,
            });
        } catch (error) {
            showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        }
    };

    useEffect(() => {
        if (!isNull(searchTerm) && !isEmpty(searchTerm)) {
            clearSelection();
            fetchSitesBySearchTerms();
        }
    }, [dropDown, appName, searchTerm]);

    useEffect(() => {
        clearSelection();
    }, [dropDown, appName]);

    return (
        <div style={{ margin: "16px" }}>
            <div
                style={{
                    display: "flex",
                    margin: "10px 0 10px 0",
                }}
            >
                <h2>Create Folders</h2>
                <AppNameToggle />
            </div>
            <div>
                <div className="counter">
                    <h4>
                        {data.pending
                            ? "Loading..."
                            : `${selectedRows.length} out of ${data.totalCount} records selected`}
                    </h4>
                </div>
                <DataGrid
                    keyExpr="hqSiteId"
                    height={calculateGridHeight(270)}
                    onSelectionChanged={onSelectionChanged}
                    selectedRowKeys={selectedRowKeys}
                    {...dataGridConfig}
                    repaintChangesOnly={false}
                    dataSource={data.sites}
                    noDataText={data.pending ? "Loading..." : "No data"}
                    allowColumnResizing={true}
                    ref={dataGridRef}
                    onRowPrepared={(e) => {
                        if (e.rowType !== "data") return;
                        if (isSiteComment || isCustomPermission) {
                            e.rowElement.style.backgroundColor = "#ffffcc";
                        } else {
                            e.rowElement.style.backgroundColor = "";
                        }
                    }}
                >
                    <Selection mode="multiple" selectAllMode={"page"} showCheckBoxesMode={"always"} />
                    <Scrolling mode="standard" />
                    <LoadPanel enabled={false} />
                    <SearchPanel
                        placeholder={"Global Search..."}
                        onTextChange={(e) => {
                            setSearchTerm(e);
                        }}
                        visible
                    />
                    <FilterRow visible={true} />
                    <HeaderFilter visible={true} />
                    <Scrolling columnRenderingMode="virtual" />
                    <Toolbar>
                        {/* <Item location="after">
                           <Button onClick={handleSyncClick} disabled={!selectedRows.length} variant="outlined">
                             Sync MDM
                           </Button>
                        </Item> */}
                        <Item location="after">
                            <FormControl size="small" variant="outlined" sx={{ width: 120 }}>
                                <Select style={{ height: 36, fontSize: "15px" }} defaultValue="External" onChange={handleChange}>
                                    {names.map((name) => (
                                        <MenuItem key={name} value={name} style={{ fontSize: "14px" }}>
                                            {name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Item>
                        <Item location="after">
                            <Button variant="outlined" disabled={!(selectedRows.length && appName !== "CS")} onClick={handleClick}>
                                Create/Update Folders
                            </Button>
                        </Item>
                        <Item location="after">
                            <Button variant="outlined" disabled={!(selectedRows.length && appName !== "CS")} onClick={handleClickForPermissions}>
                                Update Permissions
                            </Button>
                        </Item>
                        <Item location="after">
                            <Button variant="outlined" disabled={!(selectedRows.length && appName !== "CS")} onClick={handleClickForTaxYear}>
                                Update Folder MetaData
                            </Button>
                        </Item>
                        <Item name="searchPanel" location="after" />
                        <Item location="after">
                            <IconButton
                                size="medium"
                                title="Filter"
                                onClick={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setFilterPosition({ top: rect.bottom + 4, left: rect.left });
                                    setFilterAnchorEl(e.currentTarget);
                                }}
                                color={(isSiteComment || isCustomPermission) ? "primary" : "default"}
                                className={(isSiteComment || isCustomPermission) ? "filter-icon-btn--active" : "filter-icon-btn"}
                            >
                                <Badge
                                    badgeContent={[isSiteComment, isCustomPermission].filter(Boolean).length || undefined}
                                    color="primary"
                                >
                                    <FilterAltIcon fontSize="medium" />
                                </Badge>
                            </IconButton>
                        </Item>
                        <Item widget="dxButton" options={refreshButtonOptions} name="alignRight" />
                    </Toolbar>
                    <Column width="70px" alignment="left" dataField="hqSiteId" caption={"SiteID"} />
                    <Column width="70px" alignment="left" dataField="mdmClientId" caption={"MasterID"} />
                    <Column width="70px" alignment="left" dataField="mdmClientId" caption={"ClientID"} />
                    <Column dataField="hqClientName" caption={"SiteName"} />
                    <Column width="170px" dataField="siteCreatedBy" caption={"Site Created by"} />
                    <Column
                        width="190px"
                        cellRender={(e) => cellRenderForDate(e.data.siteCreated)}
                        dataField="Site Created Date / Time"
                        caption={"Site Created Date / Time"}
                    />
                    <Column cellRender={cellRenderForMessage} width="70px" dataField="status" caption={"Status"} />
                    <Column width="170px" dataField="lastModifiedBy" caption={"Last Updated by"} />
                    <Column
                        width="190px"
                        cellRender={(e) => cellRenderForDate(e.data.lastModified)}
                        dataField="Last Update Date/Time"
                        caption={"Last Update Date/Time"}
                    />
                </DataGrid>
                <Popover
                    open={Boolean(filterPosition)}
                    anchorEl={filterAnchorEl}
                    onClose={() => { setFilterPosition(null); setFilterAnchorEl(null); }}
                    anchorReference="anchorPosition"
                    anchorPosition={filterPosition ?? { top: 0, left: 0 }}
                >
                    <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: "6px", minWidth: 260 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isSiteComment}
                                    onChange={(e) => {
                                        setIsSiteComment(e.target.checked);
                                        dataGridRef.current?.instance.repaint();
                                    }}
                                    size="small"
                                />
                            }
                            label="Display sites with comments"
                            sx={{ margin: 0, "& .MuiFormControlLabel-label": { fontSize: "13px" } }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isCustomPermission}
                                    onChange={(e) => {
                                        setIsCustomPermission(e.target.checked);
                                        dataGridRef.current?.instance.repaint();
                                    }}
                                    size="small"
                                />
                            }
                            label="Display sites with custom permissions"
                            sx={{ margin: 0, "& .MuiFormControlLabel-label": { fontSize: "13px" } }}
                        />
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={!isSiteComment && !isCustomPermission}
                            onClick={() => {
                                setIsSiteComment(false);
                                setIsCustomPermission(false);
                                dataGridRef.current?.instance.repaint();
                                setFilterPosition(null);
                                setFilterAnchorEl(null);
                            }}
                            style={{ marginTop: 4 }}
                        >
                            Clear Filter
                        </Button>
                    </div>
                </Popover>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        margin: "5px 0 0 0",
                    }}
                >
                    <Pagination
                        onChange={(e: any, pageNumber: any) => {
                            setpageNumber(pageNumber);
                        }}
                        count={Math.ceil((data.totalCount as number) / defaultPageSize)}
                        variant="outlined"
                        shape="rounded"
                    />
                </div>
            </div>
        </div>
    );
};
